import {
  DICTIONARY_FIELDS,
  DICTIONARY_GROUPS,
  TAGS,
  TAG_FAMILIES,
  type TagInfo,
  type TagStats,
  tagFamilyLabel,
} from '@mollusk/shared';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { TagChip } from '../../components/TagChip';
import { MONO } from '../../theme';

export type DictionaryTab = 'fields' | 'tags';
type StatsView = { tags: Record<string, Pick<TagStats['tags'][string], 'share_10y' | 'examples'>> };

const pct = (share: number) => {
  const p = share * 100;
  return p >= 1 ? `${Math.round(p)}%` : p >= 0.1 ? `${p.toFixed(1)}%` : '<0.1%';
};

function FieldsTab() {
  return (
    <Box>
      {DICTIONARY_GROUPS.map((group) => (
        <Box key={group} sx={{ mb: 3 }}>
          <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
            {group}
          </Typography>
          <Paper sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Field</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Origin</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Example</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {DICTIONARY_FIELDS.filter((f) => f.group === group).map((f) => (
                  <TableRow key={f.name} id={`field-${f.name}`}>
                    <TableCell
                      sx={{
                        fontFamily: MONO,
                        fontSize: 13,
                        whiteSpace: 'nowrap',
                        verticalAlign: 'top',
                      }}
                    >
                      {f.name}
                    </TableCell>
                    <TableCell sx={{ fontFamily: MONO, fontSize: 12, verticalAlign: 'top' }}>
                      {f.type}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>{f.origin}</TableCell>
                    <TableCell sx={{ minWidth: 280 }}>{f.description}</TableCell>
                    <TableCell
                      sx={{
                        fontFamily: MONO,
                        fontSize: 12,
                        overflowWrap: 'anywhere',
                        minWidth: 140,
                      }}
                    >
                      {f.example ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      ))}
    </Box>
  );
}

function TagEntry({
  t,
  stats,
  highlight,
}: { t: TagInfo; stats: StatsView | undefined; highlight: boolean }) {
  const s = stats?.tags[t.tag];
  const example = s?.examples[0];
  return (
    <Paper
      data-testid={`tag-${t.tag}`}
      id={`tag-${t.tag}`}
      sx={{ p: 2, mb: 1.5, borderColor: highlight ? 'primary.main' : undefined }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography component="h3" sx={{ fontFamily: MONO, fontWeight: 600 }}>
          {t.tag}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {tagFamilyLabel(t.family)}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {s ? (
          <Typography variant="body2" color="text.secondary">
            Fires on {pct(s.share_10y)} of permits (last 10 years)
          </Typography>
        ) : null}
      </Box>
      <Typography sx={{ mt: 0.5 }}>{t.description}</Typography>
      {example ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Example: “{example.description}”
          {example.city ? ` (${example.city}${example.state ? `, ${example.state}` : ''})` : ''}
        </Typography>
      ) : null}
      {t.always_brings.length > 0 ? (
        <Box sx={{ mt: 1, display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            Always brings
          </Typography>
          {t.always_brings.map((b) => (
            <TagChip key={b} tag={b} />
          ))}
        </Box>
      ) : null}
      <Link
        href={`/tags?tag=${encodeURIComponent(t.tag)}`}
        variant="body2"
        sx={{ mt: 1, display: 'inline-block' }}
      >
        See {t.tag} on the Tags page
      </Link>
    </Paper>
  );
}

function TagsTab({ stats, focus }: { stats: StatsView | undefined; focus?: string | undefined }) {
  const [family, setFamily] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TAGS.filter(
      (t) =>
        (!family || t.family === family) &&
        (!q || t.tag.includes(q.replace(/\s+/g, '_')) || t.description.toLowerCase().includes(q)),
    );
  }, [family, query]);
  useEffect(() => {
    if (focus) document.getElementById(`tag-${focus}`)?.scrollIntoView?.({ block: 'center' });
  }, [focus]);
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', md: '220px 1fr' },
        alignItems: 'start',
      }}
    >
      <Paper sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
        <List dense aria-label="Tag families">
          <ListItemButton selected={family === null} onClick={() => setFamily(null)}>
            <ListItemText primary="All tags" secondary={`${TAGS.length}`} />
          </ListItemButton>
          {TAG_FAMILIES.map((f) => (
            <ListItemButton
              key={f.key}
              selected={family === f.key}
              onClick={() => setFamily(f.key)}
            >
              <ListItemText
                primary={f.label}
                secondary={`${TAGS.filter((t) => t.family === f.key).length}`}
              />
            </ListItemButton>
          ))}
        </List>
      </Paper>
      <Box>
        <TextField
          fullWidth
          size="small"
          label="Search tags"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { backgroundColor: 'background.paper' } }}
        />
        {shown.map((t) => (
          <TagEntry key={t.tag} t={t} stats={stats} highlight={t.tag === focus} />
        ))}
        {shown.length === 0 ? <Typography color="text.secondary">No tags match.</Typography> : null}
      </Box>
    </Box>
  );
}

/** The data dictionary (#171): Fields and Tags, from the customer dictionary. */
export function DictionaryView({
  tab,
  stats,
  focusTag,
  onTab,
}: {
  tab: DictionaryTab;
  stats: StatsView | undefined;
  focusTag?: string | undefined;
  onTab: (t: DictionaryTab) => void;
}) {
  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 1 }}>
        Data dictionary
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Every field in a Mollusk permit record, and every tag we apply.
      </Typography>
      <Tabs value={tab} onChange={(_, v: DictionaryTab) => onTab(v)} sx={{ mb: 2 }}>
        <Tab value="fields" label="Fields" />
        <Tab value="tags" label={`Tags (${TAGS.length})`} />
      </Tabs>
      {tab === 'fields' ? <FieldsTab /> : <TagsTab stats={stats} focus={focusTag} />}
    </Box>
  );
}
